import { FinishReason, getGenerativeModel } from "firebase/ai";
import { differenceInMonths, format } from "date-fns";
import { ai } from "../firebase-config";
import { IChild } from "./types/child";

export const AI_MODEL_OPTIONS = [
  { id: "gemini-3.6-flash", label: "Gemini 3.6 Flash" },
  { id: "gemini-3.1-pro-preview", label: "Gemini 3.1 Pro (Preview)" },
];

const DEFAULT_MODEL = AI_MODEL_OPTIONS[1].id;

const SYSTEM_INSTRUCTION = `You are an experienced UK early years practitioner writing the summary section of a preschool progress report that will be printed and given to the child's parents.

Write a warm, professional, parent-friendly narrative about the child's progress and development.

Rules:
- Base everything strictly on the observations provided. Never invent events, milestones, skills or concerns that are not evidenced there.
- Refer to the child by their first name. Use "they/them" unless the observations make the child's pronouns clear.
- Group the picture by what the observations show (for example communication, physical development, personal/social development, literacy, numeracy, understanding the world, creativity) rather than listing observations one by one.
- Note progress or emerging patterns over the period where the observations support it.
- Finish with a short "Next steps" paragraph, drawn from the next steps recorded in the observations.
- 3 to 5 short paragraphs. Plain prose only: no markdown, no headings, no bullet points, no bold.
- Limit the whole summary to around 150 words.
- Encouraging and factual. Avoid diagnostic or clinical language and avoid comparing the child to other children.`;

const describeAge = (dateOfBirth?: string) => {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  if (isNaN(dob.getTime())) return null;
  const months = differenceInMonths(new Date(), dob);
  if (months < 0) return null;
  return `${Math.floor(months / 12)} years ${months % 12} months`;
};

const describeObservation = (observation: any) => {
  const lines = [
    `Date: ${format(new Date(observation.observationDate), "yyyy-MM-dd")}`,
    `Area: ${observation.category || "not recorded"}`,
    `Familiarity: ${observation.familiarity || "not recorded"}`,
    `Concentration: ${observation.concentration || "not recorded"}`,
    `Notes: ${observation.observationNotes || "not recorded"}`,
  ];
  if (observation.nextSteps) lines.push(`Next steps: ${observation.nextSteps}`);
  return lines.join("\n");
};

/**
 * Builds the prompt sent to the model. Deliberately limited to the child's
 * first name, their age, and the observations on the report -- no contact
 * details, medical information or other registration data leaves the app.
 */
const buildPrompt = (child: IChild, observations: any[], fromDate?: Date | null) => {
  const firstName = child.registration.childFirstName;
  const age = describeAge(child.registration.childDateOfBirth);

  const oldest = observations[observations.length - 1];
  const newest = observations[0];
  const period = fromDate
    ? `Observations recorded from ${format(fromDate, "yyyy-MM-dd")} onwards.`
    : `All observations recorded for this child.`;

  return [
    `Child's first name: ${firstName}`,
    age ? `Age: ${age}` : null,
    period,
    `Observations cover ${format(new Date(oldest.observationDate), "yyyy-MM-dd")} to ${format(new Date(newest.observationDate), "yyyy-MM-dd")} (${observations.length} observation${observations.length === 1 ? "" : "s"}), oldest first:`,
    "",
    [...observations].reverse().map(describeObservation).join("\n\n"),
    "",
    `Write the progress summary for ${firstName}.`,
  ]
    .filter(Boolean)
    .join("\n");
};

/**
 * Generates a progress summary for the given observations. The caller is
 * responsible for passing the already date-filtered set of observations.
 */
export const generateProgressSummary = async (
  child: IChild,
  observations: any[],
  fromDate?: Date | null,
  modelName: string = DEFAULT_MODEL
): Promise<string> => {
  if (!observations || observations.length === 0) {
    throw new Error("There are no observations in the selected date range to summarise.");
  }

  const model = getGenerativeModel(ai, {
    model: modelName,
    systemInstruction: SYSTEM_INSTRUCTION,
    // 3.x Flash models think before answering, and thinking tokens count
    // towards maxOutputTokens. The old 2048 budget was exhausted by thinking
    // (often ~1900 tokens), cutting the visible summary off mid-sentence.
    // 8192 leaves plenty of room for both the reasoning and the full report.
    generationConfig: { maxOutputTokens: 8192 },
  });

  const result = await model.generateContent(buildPrompt(child, observations, fromDate));
  const response = result.response;
  const text = response.text().trim();

  if (!text) {
    throw new Error("The AI did not return a summary. Please try again.");
  }

  // Safety net: never hand over a summary that got cut off at the output
  // limit, no matter how unlikely that is with the budget above.
  if (response.candidates?.[0]?.finishReason === FinishReason.MAX_TOKENS) {
    throw new Error("The AI summary was cut off before it finished. Please try again.");
  }

  return text;
};
